import React from 'react'
import classNames from 'classnames'
import { Modal, Button, List, Icon } from 'antd'
import './AudioHistoryModal.scss'

const formatDuration = seconds => {
  if (seconds == null || seconds === '') return ''

  const totalSeconds = Math.round(Number(seconds))
  if (Number.isNaN(totalSeconds)) return ''

  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`
  }

  return `${remainingSeconds}s`
}

const formatTimestamp = timestamp => {
  if (!timestamp) return null

  const parsedDate = new Date(timestamp)
  if (Number.isNaN(parsedDate.getTime())) return null

  const date = parsedDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  const time = parsedDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })

  return {
    date,
    time,
    summary: `${date} at ${time}`
  }
}

const getVersionCountLabel = count =>
  `${count} version${count === 1 ? '' : 's'}`

const AudioHistoryModal = ({
  visible,
  history = [],
  currentAudioUrl,
  onCancel,
  onSelect
}) => {
  const handleSelectAudio = item => {
    Modal.confirm({
      title: 'Restore this audio version?',
      content:
        'This will replace the current scene audio with this saved version while keeping the rest of the history intact.',
      okText: 'Restore Version',
      cancelText: 'Cancel',
      centered: true,
      icon: <Icon type="exclamation-circle" />,
      onOk() {
        onSelect(item)
      }
    })
  }

  const reversedHistory = history.slice().reverse()
  const currentVersionIndex = reversedHistory.findIndex(
    item => item.url === currentAudioUrl
  )
  const currentVersionNumber =
    currentVersionIndex >= 0 ? history.length - currentVersionIndex : null

  return (
    <Modal
      className="audio-history-modal"
      wrapClassName="audio-history-modal-wrap"
      title={
        <div className="audio-history-modal__title">
          <div className="audio-history-modal__title-icon">
            <Icon type="customer-service" />
          </div>
          <div className="audio-history-modal__title-copy">
            <span className="audio-history-modal__title-text">
              Audio History
            </span>
            <span className="audio-history-modal__title-subtext">
              Review, compare, and restore past scene voiceovers.
            </span>
          </div>
          <span className="audio-history-modal__versions-pill">
            {getVersionCountLabel(history.length)}
          </span>
        </div>
      }
      visible={visible}
      centered
      onCancel={onCancel}
      footer={null}
      width={940}
      bodyStyle={{
        padding: 0,
        maxHeight: 'calc(100vh - 40px)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div className="audio-history-modal__body">
        <div className="audio-history-modal__shell">
          <section className="audio-history-modal__intro">
            <div className="audio-history-modal__intro-copy">
              <p className="audio-history-modal__intro-text">
                Browse saved versions and restore any previous scene audio
                without losing the rest of the history.
              </p>
            </div>

            <div className="audio-history-modal__intro-meta">
              <div className="audio-history-modal__intro-chip">
                <span>Versions</span>
                <strong>{history.length}</strong>
              </div>

              {currentVersionNumber && (
                <div className="audio-history-modal__intro-chip audio-history-modal__intro-chip--accent">
                  <span>Current</span>
                  <strong>V{currentVersionNumber}</strong>
                </div>
              )}
            </div>
          </section>

          <div className="audio-history-modal__list">
            <List
              itemLayout="horizontal"
              className="audio-history-modal__list-inner"
              dataSource={reversedHistory}
              split={false}
              locale={{
                emptyText: (
                  <div className="audio-history-modal__empty">
                    <div className="audio-history-modal__empty-icon">
                      <Icon type="inbox" />
                    </div>
                    <h4>No saved audio versions yet</h4>
                    <p>
                      Generate scene audio and each version will appear here for
                      playback and quick restore.
                    </p>
                  </div>
                )
              }}
              renderItem={(item, index) => {
                const versionNumber = history.length - index
                const isCurrent = item.url === currentAudioUrl
                const isLatest = index === 0
                const timestampInfo = formatTimestamp(item.timestamp)
                const durationLabel = formatDuration(item.duration)

                const summaryText = timestampInfo
                  ? `Saved ${timestampInfo.summary}`
                  : 'Saved audio snapshot available for restore.'

                return (
                  <List.Item
                    key={
                      item.id || item.url || `audio-version-${versionNumber}`
                    }
                    className={classNames('audio-history-card', {
                      'audio-history-card--current': isCurrent,
                      'audio-history-card--latest': isLatest
                    })}
                  >
                    <div className="audio-history-card__surface">
                      <div className="audio-history-card__header">
                        <div className="audio-history-card__header-copy">
                          <span className="audio-history-card__eyebrow">
                            Audio version
                          </span>
                          <div className="audio-history-card__title-row">
                            <h4>Version {versionNumber}</h4>

                            {isCurrent && (
                              <span className="audio-history-card__status audio-history-card__status--current">
                                Current
                              </span>
                            )}

                            {!isCurrent && isLatest && (
                              <span className="audio-history-card__status audio-history-card__status--latest">
                                Latest
                              </span>
                            )}
                          </div>

                          <p className="audio-history-card__summary">
                            {summaryText}
                          </p>
                        </div>

                        <Button
                          type={isCurrent ? 'default' : 'primary'}
                          icon={isCurrent ? 'check' : 'rollback'}
                          onClick={() => handleSelectAudio(item)}
                          disabled={isCurrent}
                          className={classNames(
                            'audio-history-card__restore-btn',
                            {
                              'audio-history-card__restore-btn--current': isCurrent
                            }
                          )}
                        >
                          {isCurrent ? 'Currently in Use' : 'Restore Version'}
                        </Button>
                      </div>

                      <div className="audio-history-card__content">
                        {(timestampInfo || durationLabel) && (
                          <div className="audio-history-card__meta">
                            {timestampInfo && (
                              <div className="audio-history-card__meta-item">
                                <Icon type="clock-circle" />
                                <span>{timestampInfo.summary}</span>
                              </div>
                            )}

                            {durationLabel && (
                              <div className="audio-history-card__meta-item">
                                <Icon type="customer-service" />
                                <span>{durationLabel}</span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="audio-history-card__player-shell">
                          <audio
                            controls
                            src={item.url}
                            className="audio-history-card__player"
                          />
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AudioHistoryModal
